/**
 * Cart Recommendations Component
 * Handles dynamic product recommendations using Shopify's Product Recommendations API
 */
import { ThemeEvents, CartUpdateEvent } from '@theme/events';

class CartRecommendations extends HTMLElement {
  constructor() {
    super();
    this.container = this.querySelector('.cart_recommendations_container');
    this.staticRecommendations = this.querySelector('.static-recommendations');
    this.dynamicRecommendations = this.querySelector('.dynamic-recommendations');
    this.isInitialized = false;
    this.cachedCart = null;
    this.currentState = 'static'; // Track current display state
  }

  connectedCallback() {
    // Prevent morphing of this component - use the theme's data attribute
    this.setAttribute('data-skip-subtree-update', 'true');
    
    // Store original heading text
    const heading = this.querySelector('.cart-recommendations__title');
    if (heading && !heading.hasAttribute('data-original-text')) {
      heading.setAttribute('data-original-text', heading.textContent.trim());
    }
    
    // Delay initialization slightly to allow cart to load
    requestAnimationFrame(() => {
      this.initialize();
    });
    
    // Listen for cart updates using theme events
    document.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdateEvent);
    
    // Listen for when cart drawer opens
    const cartDrawer = this.closest('cart-drawer-component');
    if (cartDrawer) {
      const dialog = cartDrawer.querySelector('dialog');
      if (dialog) {
        const observer = new MutationObserver((mutations) => {
          mutations.forEach((mutation) => {
            if (mutation.attributeName === 'open' && dialog.hasAttribute('open')) {
              // Cart drawer opened - refresh recommendations if needed
              if (!this.isInitialized) {
                this.initialize();
              }
            }
          });
        });
        observer.observe(dialog, { attributes: true });
        this.drawerObserver = observer;
      }
    }
  }

  disconnectedCallback() {
    document.removeEventListener(ThemeEvents.cartUpdate, this.handleCartUpdateEvent);
    if (this.drawerObserver) {
      this.drawerObserver.disconnect();
    }
  }

  handleCartUpdateEvent = (event) => {
    this.handleCartUpdate();
  };

  async initialize() {
    if (this.isInitialized) return;
    
    // First, try to get cart from sessionStorage for instant display
    const cachedCartData = sessionStorage.getItem('cart');
    let initialCart = null;
    
    if (cachedCartData) {
      try {
        initialCart = JSON.parse(cachedCartData);
        this.cachedCart = initialCart;
        
        // Show appropriate recommendations based on cache immediately
        if (initialCart.item_count === 0) {
          this.showStaticRecommendations();
        } else {
          await this.showDynamicRecommendations(initialCart);
        }
      } catch (e) {
        console.error('Error parsing cached cart:', e);
      }
    }
    
    // Always fetch fresh cart data to ensure accuracy
    try {
      const cart = await this.fetchCart();
      
      // Cache the fresh cart data
      if (cart) {
        sessionStorage.setItem('cart', JSON.stringify(cart));
        this.cachedCart = cart;
      }
      
      // Update display with fresh data if it differs from cache
      if (!initialCart || cart.item_count !== initialCart.item_count) {
        if (!cart || cart.item_count === 0) {
          this.showStaticRecommendations();
        } else {
          await this.showDynamicRecommendations(cart);
        }
      }
      
      this.isInitialized = true;
    } catch (error) {
      console.error('Error fetching cart:', error);
      if (!initialCart) {
        this.showStaticRecommendations();
      }
      this.isInitialized = true;
    }
  }

  async handleCartUpdate() {
    // Clear cache when cart updates
    sessionStorage.removeItem('cart');
    await this.updateRecommendations();
  }

  async updateRecommendations() {
    try {
      const cart = await this.fetchCart();
      
      // Cache the cart data
      if (cart) {
        sessionStorage.setItem('cart', JSON.stringify(cart));
        this.cachedCart = cart;
      }
      
      if (!cart || cart.item_count === 0) {
        this.showStaticRecommendations();
      } else {
        await this.showDynamicRecommendations(cart);
      }
      
      this.isInitialized = true;
    } catch (error) {
      console.error('Error updating recommendations:', error);
      this.showStaticRecommendations();
    }
  }

  async fetchCart() {
    const response = await fetch('/cart.js');
    const cart = await response.json();
    return cart;
  }

  showStaticRecommendations() {
    if (this.currentState === 'static') {
      return;
    }
    
    this.currentState = 'static';
    
    if (this.staticRecommendations) {
      this.staticRecommendations.style.display = 'block';
    }
    if (this.dynamicRecommendations) {
      this.dynamicRecommendations.style.display = 'none';
    }
    
    // Reset heading to original for static recommendations
    const heading = this.querySelector('.cart-recommendations__title');
    if (heading && heading.hasAttribute('data-original-text')) {
      heading.textContent = heading.getAttribute('data-original-text');
    }
  }

  async showDynamicRecommendations(cart) {
    if (!this.dynamicRecommendations) return;

    // Get the first product in cart for recommendations
    const firstItem = cart.items[0];
    if (!firstItem) {
      this.showStaticRecommendations();
      return;
    }

    // Fetch recommendations from Shopify API
    const recommendations = await this.fetchRecommendations(firstItem.product_id);
    
    if (recommendations && recommendations.length > 0) {
      await this.renderDynamicRecommendations(recommendations, cart);
      if (this.staticRecommendations) {
        this.staticRecommendations.style.display = 'none';
      }
      this.dynamicRecommendations.style.display = 'block';
      this.currentState = 'dynamic';
    } else {
      this.showStaticRecommendations();
    }
  }

  async fetchRecommendations(productId) {
    try {
      const response = await fetch(
        `/recommendations/products.json?product_id=${productId}&limit=4`
      );
      
      if (!response.ok) throw new Error('Failed to fetch recommendations');
      
      const data = await response.json();
      return data.products || [];
    } catch (error) {
      console.error('Error fetching recommendations:', error);
      return [];
    }
  }

  async renderDynamicRecommendations(products, cart) {
    const cartProductIds = cart.items.map(item => item.product_id);
    
    // Filter out products already in cart
    const filteredProducts = products.filter(p => !cartProductIds.includes(p.id));
    
    if (filteredProducts.length === 0) {
      this.showStaticRecommendations();
      return;
    }

    const list = this.dynamicRecommendations?.querySelector('.mini-cart__navigation');
    if (!list) return;

    list.innerHTML = filteredProducts.map(product => this.renderProductCard(product)).join('');
    
    // Update the heading for dynamic recommendations
    const heading = this.querySelector('.cart-recommendations__title');
    if (heading) {
      heading.textContent = 'Complete Your Look With';
    }
    
    // Re-initialize add-to-cart buttons
    this.initializeQuickAdd();
  }

  renderProductCard(product) {
    const variant = product.variants[0];
    const price = this.formatMoney(variant.price);
    const productFormId = `QuickAdd-ProductForm-${product.id}-cart-rec`;
    
    // Handle image - Shopify API returns 'featured_image' as a string URL
    const hasImage = product.featured_image;
    const imageUrl = product.featured_image;
    const imageAlt = product.title;
    
    // Calculate aspect ratio from image if available, default to 1
    const aspectRatio = product.featured_media?.aspect_ratio || 1;
    const imageHeight = Math.ceil(120 / aspectRatio);
    
    return `
      <li>
        <div class="product-container ${!hasImage ? 'no-image' : ''}">
          ${hasImage ? `
            <div class="product-image">
              <a href="${product.url}" class="media-wrapper media-wrapper--small">
                <div class="media media--adapt" style="--image-ratio-percent: ${(100 / aspectRatio)}%;">
                  <img 
                    srcset="${imageUrl}&width=120 1x, ${imageUrl}&width=240 2x"
                    src="${imageUrl}&width=120"
                    alt="${this.escapeHtml(imageAlt)}"
                    width="120"
                    height="${imageHeight}"
                    loading="lazy"
                  />
                </div>
              </a>
            </div>
          ` : ''}
          <div class="product-description">
            <div class="product-content">
              <a href="${product.url}" class="link product-title">${this.escapeHtml(product.title)}</a>
            </div>
            <div class="price-wrapper">
              <span class="price-item price-item--sale">${price}</span>
            </div>
            <div class="product-button">
              ${product.variants.length === 1 ? `
                <button 
                  class="recmmendations-button add-to-cart-btn"
                  data-variant-id="${variant.id}"
                  data-available="${product.available}"
                  type="button"
                  ${!product.available ? 'disabled' : ''}
                >
                  ${product.available ? 'Add to cart' : 'Sold out'}
                </button>
                <a href="${product.url}" class="recmmendations-button">
                  View details
                </a>
              ` : `
                <a href="${product.url}" class="recmmendations-button">
                  View details
                </a>
              `}
              ${product.available && product.variants.length > 1 ? this.renderQuickAddButton(product, productFormId) : ''}
            </div>
          </div>
        </div>
      </li>
    `;
  }

  renderQuickAddButton(product, productFormId) {
    const variant = product.variants[0];
    
    return `
      <quick-add-component
        class="quick-add"
        ref="quickAdd"
        data-product-title="${this.escapeHtml(product.title)}"
        data-product-url="${product.url}"
      >
        <product-form-component
          data-section-id="cart-recommendations"
          data-product-id="${product.id}"
          class="quick-add__product-form-component quick-add__product-form-component--multi-variant"
        >
          <div class="visually-hidden" aria-live="assertive" role="status" aria-atomic="true" ref="liveRegion"></div>
          <form method="post" action="/cart/add" id="${productFormId}" accept-charset="UTF-8" class="shopify-product-form" enctype="multipart/form-data" novalidate="novalidate" data-type="add-to-cart-form">
            <input type="hidden" name="form_type" value="product">
            <input type="hidden" name="utf8" value="✓">
            <input type="hidden" name="id" ref="variantId" value="${variant.id}">
            <input type="hidden" name="quantity" value="1">
            <button
              class="button quick-add__button quick-add__button--choose"
              type="button"
              name="add"
              data-quick-add-trigger
            >
              <span class="add-to-cart-text">
                <span class="cart_quick_add_text">Add to cart</span>
              </span>
            </button>
          </form>
        </product-form-component>
      </quick-add-component>
    `;
  }

  formatMoney(cents) {
    const amount = cents / 100;
    const currencyFormat = window.Shopify?.currency?.active || 'USD';
    return new Intl.NumberFormat('en', {
      style: 'currency',
      currency: currencyFormat
    }).format(amount);
  }

  escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  initializeQuickAdd() {
    // Initialize single-variant add-to-cart buttons
    this.dynamicRecommendations?.querySelectorAll('.add-to-cart-btn').forEach(button => {
      button.addEventListener('click', async (e) => {
        e.preventDefault();
        const variantId = button.dataset.variantId;
        const isAvailable = button.dataset.available === 'true';
        if (variantId && isAvailable) {
          await this.addToCart(variantId, button);
        }
      });
    });

    // Initialize multi-variant quick-add modal triggers
    this.dynamicRecommendations?.querySelectorAll('[data-quick-add-trigger]').forEach(button => {
      button.addEventListener('click', (e) => {
        e.preventDefault();
        const quickAddComponent = button.closest('quick-add-component');
        if (quickAddComponent && quickAddComponent.handleClick) {
          quickAddComponent.handleClick(e);
        }
      });
    });
  }

  async addToCart(variantId, button) {
    try {
      button.disabled = true;
      const originalText = button.textContent;
      button.textContent = 'Adding...';
      
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          items: [{
            id: variantId,
            quantity: 1
          }]
        })
      });

      if (!response.ok) throw new Error('Failed to add to cart');

      const data = await response.json();

      // Clear session storage cache
      sessionStorage.removeItem('cart');

      // Dispatch cart update event using theme events
      document.dispatchEvent(
        new CartUpdateEvent(data, 'cart-recommendations', {
          itemCount: data.item_count || 1,
          variantId: variantId
        })
      );

      button.textContent = 'Added!';
      setTimeout(() => {
        button.disabled = false;
        button.textContent = originalText;
      }, 1000);
    } catch (error) {
      console.error('Error adding to cart:', error);
      button.disabled = false;
      button.textContent = 'Add to cart';
    }
  }
}

customElements.define('cart-recommendations', CartRecommendations);

/**
 * Mobile Cart Recommendations Component
 * Handles dynamic product recommendations for mobile cart drawer
 */
class CartRecommendationsMobile extends HTMLElement {
  constructor() {
    super();
    this.staticRecommendations = this.querySelector('.static-recommendations-mobile');
    this.dynamicRecommendations = this.querySelector('.dynamic-recommendations-mobile');
    this.isInitialized = false;
    this.cachedCart = null;
    this.currentState = 'static';
  }

  connectedCallback() {
    this.setAttribute('data-skip-subtree-update', 'true');
    
    const heading = this.querySelector('.mobile-recommendations__title');
    if (heading && !heading.hasAttribute('data-original-text')) {
      heading.setAttribute('data-original-text', heading.textContent.trim());
    }
    
    requestAnimationFrame(() => {
      this.initialize();
    });
    
    document.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdateEvent);
    
    const cartDrawer = this.closest('cart-drawer-component');
    if (cartDrawer) {
      const dialog = cartDrawer.querySelector('dialog');
      if (dialog) {
        const observer = new MutationObserver((mutations) => {
          mutations.forEach((mutation) => {
            if (mutation.attributeName === 'open' && dialog.hasAttribute('open')) {
              if (!this.isInitialized) {
                this.initialize();
              }
            }
          });
        });
        observer.observe(dialog, { attributes: true });
        this.drawerObserver = observer;
      }
    }
  }

  disconnectedCallback() {
    document.removeEventListener(ThemeEvents.cartUpdate, this.handleCartUpdateEvent);
    if (this.drawerObserver) {
      this.drawerObserver.disconnect();
    }
  }

  handleCartUpdateEvent = (event) => {
    this.handleCartUpdate();
  };

  async handleCartUpdate() {
    sessionStorage.removeItem('cart');
    await this.updateRecommendations();
  }

  async updateRecommendations() {
    try {
      const cart = await this.fetchCart();
      
      if (cart) {
        sessionStorage.setItem('cart', JSON.stringify(cart));
        this.cachedCart = cart;
      }
      
      if (!cart || cart.item_count === 0) {
        this.showStaticRecommendations();
      } else {
        await this.showDynamicRecommendations(cart);
      }
      
      this.isInitialized = true;
    } catch (error) {
      console.error('Error updating recommendations:', error);
      this.showStaticRecommendations();
    }
  }

  async initialize() {
    if (this.isInitialized) return;
    
    const cachedCartData = sessionStorage.getItem('cart');
    let initialCart = null;
    
    if (cachedCartData) {
      try {
        initialCart = JSON.parse(cachedCartData);
        this.cachedCart = initialCart;
        
        if (initialCart.item_count === 0) {
          this.showStaticRecommendations();
        } else {
          await this.showDynamicRecommendations(initialCart);
        }
      } catch (e) {
        console.error('Error parsing cached cart:', e);
      }
    }
    
    try {
      const cart = await this.fetchCart();
      
      if (cart) {
        sessionStorage.setItem('cart', JSON.stringify(cart));
        this.cachedCart = cart;
      }
      
      if (!initialCart || cart.item_count !== initialCart.item_count) {
        if (!cart || cart.item_count === 0) {
          this.showStaticRecommendations();
        } else {
          await this.showDynamicRecommendations(cart);
        }
      }
      
      this.isInitialized = true;
    } catch (error) {
      console.error('Error fetching cart:', error);
      if (!initialCart) {
        this.showStaticRecommendations();
      }
      this.isInitialized = true;
    }
  }

  async fetchCart() {
    const response = await fetch('/cart.js');
    const cart = await response.json();
    return cart;
  }

  showStaticRecommendations() {
    if (this.currentState === 'static') return;
    
    this.currentState = 'static';
    
    if (this.staticRecommendations) {
      this.staticRecommendations.style.display = 'block';
    }
    if (this.dynamicRecommendations) {
      this.dynamicRecommendations.style.display = 'none';
    }
    
    const heading = this.querySelector('.mobile-recommendations__title');
    if (heading && heading.hasAttribute('data-original-text')) {
      heading.textContent = heading.getAttribute('data-original-text');
    }
  }

  async showDynamicRecommendations(cart) {
    if (!this.dynamicRecommendations) return;

    const firstItem = cart.items[0];
    if (!firstItem) {
      this.showStaticRecommendations();
      return;
    }

    const recommendations = await this.fetchRecommendations(firstItem.product_id);
    
    if (recommendations && recommendations.length > 0) {
      await this.renderDynamicRecommendations(recommendations, cart);
      if (this.staticRecommendations) {
        this.staticRecommendations.style.display = 'none';
      }
      this.dynamicRecommendations.style.display = 'block';
      this.currentState = 'dynamic';
      
      const heading = this.querySelector('.mobile-recommendations__title');
      if (heading) {
        heading.textContent = 'Complete Your Look With';
      }
    } else {
      this.showStaticRecommendations();
    }
  }

  async fetchRecommendations(productId) {
    try {
      const response = await fetch(
        `/recommendations/products.json?product_id=${productId}&limit=4`
      );
      
      if (!response.ok) throw new Error('Failed to fetch recommendations');
      
      const data = await response.json();
      return data.products || [];
    } catch (error) {
      console.error('Error fetching recommendations:', error);
      return [];
    }
  }

  async renderDynamicRecommendations(products, cart) {
    const cartProductIds = cart.items.map(item => item.product_id);
    const filteredProducts = products.filter(p => !cartProductIds.includes(p.id));
    
    if (filteredProducts.length === 0) {
      this.showStaticRecommendations();
      return;
    }

    const list = this.dynamicRecommendations?.querySelector('.mobile-recommendations__list');
    if (!list) return;

    list.innerHTML = filteredProducts.map(product => this.renderProductCard(product)).join('');
    this.initializeButtons();
  }

  renderProductCard(product) {
    const variant = product.variants[0];
    const price = this.formatMoney(variant.price);
    const hasImage = product.featured_image;
    const imageUrl = product.featured_image;
    const imageAlt = product.title;
    const aspectRatio = product.featured_media?.aspect_ratio || 1;
    const productFormId = `QuickAdd-ProductForm-${product.id}-mobile-rec`;
    
    return `
      <div class="mobile-recommendations__item">
        <div class="product-container ${!hasImage ? 'no-image' : ''}">
          ${hasImage ? `
            <div class="product-image">
              <a href="${product.url}" class="media-wrapper media-wrapper--small">
                <div class="media media--adapt" style="--image-ratio-percent: ${(100 / aspectRatio)}%;">
                  <img 
                    srcset="${imageUrl}&width=120 1x, ${imageUrl}&width=240 2x"
                    src="${imageUrl}&width=120"
                    alt="${this.escapeHtml(imageAlt)}"
                    width="120"
                    height="120"
                    loading="lazy"
                  />
                </div>
              </a>
            </div>
          ` : ''}
          <div class="product-description">
            <div class="product-content">
              <a href="${product.url}" class="link product-title">${this.escapeHtml(product.title)}</a>
            </div>
            <div class="price-wrapper">
              <span class="price-item price-item--sale">${price}</span>
            </div>
            <div class="product-button">
              ${product.variants.length === 1 ? `
                <button 
                  class="recmmendations-button add-to-cart-btn-mobile"
                  data-variant-id="${variant.id}"
                  data-available="${product.available}"
                  type="button"
                  ${!product.available ? 'disabled' : ''}
                >
                  ${product.available ? 'Add to cart' : 'Sold out'}
                </button>
                <a href="${product.url}" class="recmmendations-button">
                  View details
                </a>
              ` : `
                <a href="${product.url}" class="recmmendations-button">
                  View details
                </a>
              `}
              ${product.available && product.variants.length > 1 ? this.renderQuickAddButton(product, productFormId) : ''}
            </div>
          </div>
        </div>
      </div>
    `;
  }

  renderQuickAddButton(product, productFormId) {
    const variant = product.variants[0];
    
    return `
      <quick-add-component
        class="quick-add"
        ref="quickAdd"
        data-product-title="${this.escapeHtml(product.title)}"
        data-product-url="${product.url}"
      >
        <product-form-component
          data-section-id="cart-recommendations-mobile"
          data-product-id="${product.id}"
          class="quick-add__product-form-component quick-add__product-form-component--multi-variant"
        >
          <div class="visually-hidden" aria-live="assertive" role="status" aria-atomic="true" ref="liveRegion"></div>
          <form method="post" action="/cart/add" id="${productFormId}" accept-charset="UTF-8" class="shopify-product-form" enctype="multipart/form-data" novalidate="novalidate" data-type="add-to-cart-form">
            <input type="hidden" name="form_type" value="product">
            <input type="hidden" name="utf8" value="✓">
            <input type="hidden" name="id" ref="variantId" value="${variant.id}">
            <input type="hidden" name="quantity" value="1">
            <button
              class="button quick-add__button quick-add__button--choose"
              type="button"
              name="add"
              data-quick-add-trigger-mobile
            >
              <span class="add-to-cart-text">
                <span class="cart_quick_add_text">Add to cart</span>
              </span>
            </button>
          </form>
        </product-form-component>
      </quick-add-component>
    `;
  }

  formatMoney(cents) {
    const amount = cents / 100;
    const currencyFormat = window.Shopify?.currency?.active || 'USD';
    return new Intl.NumberFormat('en', {
      style: 'currency',
      currency: currencyFormat
    }).format(amount);
  }

  escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  initializeButtons() {
    // Initialize single-variant add-to-cart buttons
    this.dynamicRecommendations?.querySelectorAll('.add-to-cart-btn-mobile').forEach(button => {
      button.addEventListener('click', async (e) => {
        e.preventDefault();
        const variantId = button.dataset.variantId;
        const isAvailable = button.dataset.available === 'true';
        if (variantId && isAvailable) {
          await this.addToCart(variantId, button);
        }
      });
    });

    // Initialize multi-variant quick-add modal triggers
    this.dynamicRecommendations?.querySelectorAll('[data-quick-add-trigger-mobile]').forEach(button => {
      button.addEventListener('click', (e) => {
        e.preventDefault();
        const quickAddComponent = button.closest('quick-add-component');
        if (quickAddComponent && quickAddComponent.handleClick) {
          quickAddComponent.handleClick(e);
        }
      });
    });
  }

  async addToCart(variantId, button) {
    try {
      button.disabled = true;
      const originalText = button.textContent;
      button.textContent = 'Adding...';
      
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          items: [{
            id: variantId,
            quantity: 1
          }]
        })
      });

      if (!response.ok) throw new Error('Failed to add to cart');

      const data = await response.json();
      sessionStorage.removeItem('cart');

      document.dispatchEvent(
        new CartUpdateEvent(data, 'cart-recommendations-mobile', {
          itemCount: data.item_count || 1,
          variantId: variantId
        })
      );

      button.textContent = 'Added!';
      setTimeout(() => {
        button.disabled = false;
        button.textContent = originalText;
      }, 1000);
    } catch (error) {
      console.error('Error adding to cart:', error);
      button.disabled = false;
      button.textContent = 'Add to cart';
    }
  }
}

customElements.define('cart-recommendations-mobile', CartRecommendationsMobile);
